import { useState, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  listContent,
  archiveContent,
  deleteContent,
  logout,
  getMe,
  type ContentItem,
} from '../api'

// ────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────

function TypeBadge({ type }: { type: string }) {
  const cls =
    type === 'html'
      ? 'badge-html'
      : type === 'pdf'
      ? 'badge-pdf'
      : 'badge-ppt'
  return <span className={cls}>{type.toUpperCase()}</span>
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// ────────────────────────────────────────────────────────────
// Debounce hook
// ────────────────────────────────────────────────────────────

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value)
  const ref = useCallback(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useState(ref)
  return debounced
}

// ────────────────────────────────────────────────────────────
// Dashboard
// ────────────────────────────────────────────────────────────

type FilterTab = 'all' | 'html' | 'pdf' | 'ppt' | 'archived'

export default function Dashboard() {
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [search, setSearch] = useState('')
  const [tab, setTab] = useState<FilterTab>('all')
  const [page, setPage] = useState(1)
  const [copied, setCopied] = useState<string | null>(null)

  const debouncedSearch = useDebounce(search, 350)

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: getMe })

  const { data, isLoading } = useQuery({
    queryKey: ['content', tab, debouncedSearch, page],
    queryFn: () =>
      listContent({
        q: debouncedSearch || undefined,
        type: tab !== 'all' && tab !== 'archived' ? tab : undefined,
        archived: tab === 'archived',
        page,
        pageSize: 20,
      }),
    placeholderData: (prev) => prev,
  })

  const archiveMut = useMutation({
    mutationFn: archiveContent,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['content'] }),
  })

  const deleteMut = useMutation({
    mutationFn: deleteContent,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['content'] }),
  })

  async function handleLogout() {
    await logout()
    navigate('/admin/login', { replace: true })
  }

  function copyLink(slug: string) {
    const url = `https://aibens.cc/v/${slug}`
    navigator.clipboard.writeText(url).then(() => {
      setCopied(slug)
      setTimeout(() => setCopied(null), 2000)
    })
  }

  function confirmDelete(item: ContentItem) {
    if (window.confirm(`确认删除「${item.title}」？此操作不可撤销。`)) {
      deleteMut.mutate(item.id)
    }
  }

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = Math.ceil(total / 20)

  // Stats from current non-archived content
  const typeCounts = { html: 0, pdf: 0, ppt: 0 }
  items.forEach((i) => {
    if (i.type in typeCounts) typeCounts[i.type as keyof typeof typeCounts]++
  })

  const tabs: { key: FilterTab; label: string }[] = [
    { key: 'all', label: '全部' },
    { key: 'html', label: 'HTML' },
    { key: 'pdf', label: 'PDF' },
    { key: 'ppt', label: 'PPT' },
    { key: 'archived', label: '已归档' },
  ]

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="border-b border-gray-800 bg-gray-950 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <span className="font-semibold text-white">AIBens.CC</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500 hidden sm:block">
              {me?.phone}
              {me?.role === 'admin' && (
                <span className="ml-1.5 text-blue-400 font-medium">管理员</span>
              )}
            </span>
            <Link to="/admin/upload" className="btn-primary text-xs px-3 py-1.5">
              + 上传内容
            </Link>
            <button onClick={handleLogout} className="btn-secondary text-xs px-3 py-1.5">
              退出
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: '总内容', value: total, color: 'text-white' },
            { label: 'HTML', value: typeCounts.html, color: 'text-emerald-400' },
            { label: 'PDF', value: typeCounts.pdf, color: 'text-red-400' },
            { label: 'PPT', value: typeCounts.ppt, color: 'text-orange-400' },
          ].map((stat) => (
            <div key={stat.label} className="card py-4">
              <p className="text-xs text-gray-500 mb-1">{stat.label}</p>
              <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            placeholder="搜索标题..."
            className="input max-w-xs"
          />
          <div className="flex gap-1 bg-gray-900 border border-gray-800 rounded-lg p-1">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => { setTab(t.key); setPage(1) }}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  tab === t.key
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-400 hover:text-gray-200'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="card p-0 overflow-hidden">
          {isLoading ? (
            <div className="py-16 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-gray-500">
              <p className="text-lg">暂无内容</p>
              <p className="text-sm mt-1">点击「上传内容」开始添加</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left px-4 py-3 text-gray-400 font-medium">标题</th>
                  <th className="text-left px-4 py-3 text-gray-400 font-medium hidden sm:table-cell">类型</th>
                  <th className="text-left px-4 py-3 text-gray-400 font-medium hidden md:table-cell">标签</th>
                  <th className="text-left px-4 py-3 text-gray-400 font-medium hidden lg:table-cell">大小</th>
                  <th className="text-left px-4 py-3 text-gray-400 font-medium hidden lg:table-cell">创建时间</th>
                  <th className="text-right px-4 py-3 text-gray-400 font-medium">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/50">
                {items.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-800/30 transition-colors">
                    <td className="px-4 py-3">
                      <Link
                        to={`/admin/content/${item.id}`}
                        className="font-medium text-gray-100 hover:text-blue-400 transition-colors line-clamp-1"
                      >
                        {item.title}
                      </Link>
                      {item.archived && (
                        <span className="ml-2 text-xs text-gray-500">[已归档]</span>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <TypeBadge type={item.type} />
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {item.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="text-xs text-gray-400 bg-gray-800 px-1.5 py-0.5 rounded">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-400 hidden lg:table-cell">
                      {formatBytes(item.size_bytes)}
                    </td>
                    <td className="px-4 py-3 text-gray-400 hidden lg:table-cell text-xs">
                      {formatDate(item.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {/* Copy link */}
                        <button
                          onClick={() => copyLink(item.slug)}
                          title="复制链接"
                          className="p-1.5 rounded hover:bg-gray-700 text-gray-400 hover:text-gray-200 transition-colors"
                        >
                          {copied === item.slug ? (
                            <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          )}
                        </button>
                        {/* Edit */}
                        <Link
                          to={`/admin/content/${item.id}`}
                          title="编辑"
                          className="p-1.5 rounded hover:bg-gray-700 text-gray-400 hover:text-gray-200 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </Link>
                        {/* Archive */}
                        <button
                          onClick={() => archiveMut.mutate(item.id)}
                          title={item.archived ? '取消归档' : '归档'}
                          className="p-1.5 rounded hover:bg-gray-700 text-gray-400 hover:text-yellow-400 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                          </svg>
                        </button>
                        {/* Delete */}
                        <button
                          onClick={() => confirmDelete(item)}
                          title="删除"
                          className="p-1.5 rounded hover:bg-gray-700 text-gray-400 hover:text-red-400 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between text-sm text-gray-400">
            <span>共 {total} 条</span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="btn-secondary px-3 py-1.5 text-xs"
              >
                上一页
              </button>
              <span className="px-3 py-1.5">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="btn-secondary px-3 py-1.5 text-xs"
              >
                下一页
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
